import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { unApi } from '../../shared/common/api/endpoints/reports/unApi';
import { showErrorMessage, getApiErrorMessage } from './ui';

interface Dependent {
    report_id: number;
    name: string;
}

type DeleteResult = 'deleted' | 'cancelled' | 'error';

/**
 * Удаляет группу регистраций с проверкой зависимостей.
 * Если группа используется осью в сохранённых отчётах — бэк возвращает
 * requires_confirmation со списком отчётов; тогда показываем предупреждение и
 * каскадно удаляем (вычищаем ссылки из отчётов + саму группу) только по подтверждению.
 * Если группа нигде не используется — удаляется сразу, без модалки.
 *
 * @param groupId  id удаляемой группы
 * @param pathname текущий путь (заголовок Pathname)
 * @param options.onDeleted вызывается с телом ответа об успешном удалении
 * @returns 'deleted' | 'cancelled' | 'error'
 */
export const deleteCustomGroupWithConfirm = async (
    groupId: number | string,
    pathname: string,
    options: { onDeleted: (data: any) => void | Promise<void> }
): Promise<DeleteResult> => {
    let data: any;
    try {
        const firstRes = await unApi.removeCustomGroup(groupId, pathname);
        // Бэк отвечает конвертом { success, code, message, data: {...} } — берём вложенный data
        // (с фолбэком на плоский ответ, если конверта нет)
        const envelope = firstRes?.data || {};
        data = envelope.data ?? envelope;
    } catch (e) {
        showErrorMessage(getApiErrorMessage(e, 'Не удалось удалить группу.'));
        return 'error';
    }

    // Группа используется в отчётах — НЕ удалена, спрашиваем подтверждение
    if (data.requires_confirmation) {
        const dependents: Dependent[] = data.dependents || [];
        return new Promise<DeleteResult>((resolve) => {
            Modal.confirm({
                title: 'Группа используется в отчётах',
                icon: <ExclamationCircleOutlined />,
                width: 520,
                content: (
                    <div>
                        <p>
                            Эта группа регистраций используется осью в сохранённых отчётах. Если
                            удалить, ссылки на неё будут вычищены из этих отчётов:
                        </p>
                        <ul style={{ marginBottom: 12 }}>
                            {dependents.map((dep) => (
                                <li key={dep.report_id}>
                                    <b>{dep.name || `#${dep.report_id}`}</b>
                                </li>
                            ))}
                        </ul>
                        <p>Удалить группу и очистить ссылки в отчётах?</p>
                    </div>
                ),
                okText: 'Удалить всё',
                cancelText: 'Отмена',
                okButtonProps: { danger: true },
                onOk: async () => {
                    try {
                        const confirmedRes = await unApi.removeCustomGroup(groupId, pathname, true);
                        const confirmedEnvelope = confirmedRes?.data || {};
                        await options.onDeleted(confirmedEnvelope.data ?? confirmedEnvelope);
                        resolve('deleted');
                    } catch (e) {
                        showErrorMessage(getApiErrorMessage(e, 'Не удалось удалить группу.'));
                        resolve('error');
                    }
                },
                onCancel: () => resolve('cancelled'),
            });
        });
    }

    // Зависимых нет — группа удалена сразу
    await options.onDeleted(data);
    return 'deleted';
};
